<template>
  <div class="homepage">
    <DashboardStatistics
      :data="statisticObjects"
    />
    <el-row
      v-if="allCategories.length > 0 && !loading"
      :gutter="24"
    >
      <el-col
        :lg="12"
        class="mb-xl"
      >
        <DashboardPieChart
          :title="'Thống kê hiện vật theo kho'"
          :data="statisticObjects1"
          :height="450"
        />
      </el-col>
      <el-col
        :lg="12"
        class="mb-xl"
      >
        <DashboardPieChart
          :title="'Thống kê hiện vật theo hiện trạng'"
          :data="statisticObjects2"
          :height="450"
        />
      </el-col>
      <el-col :lg="24">
        <DashboardColumnChart
          :title="'Thống kê theo thời gian'"
          :data="timeObjects"
        />
      </el-col>
    </el-row>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { mapGetters } from "vuex";
import DashboardStatistics from "./components/Statistics.vue";
import DashboardPieChart from "./components/PieChart.vue";
import DashboardColumnChart from "./components/ColumnChart.vue";

import constants, { ROLE_ACTIONS } from "@/config/constants";
import organizationServices from "@/services/organization"

import documentUtilServices from "@/services/document-util";

export default {
  name: "Dashboard",
  components: {
    DashboardStatistics,
    DashboardPieChart,
    DashboardColumnChart
  },
  data() {
    return {
      loading: true,
      statistic: {},
      timeData: {},
      statistic1: {},
      statistic2: {},
      organizationId: organizationServices.getCurrentOrg()?.id
    }
  },
  computed: {
    ...mapGetters("config", ["allConfigs"]),
    ...mapGetters("category", ["allCategories"]),
    objects() {
      return this.allConfigs.filter((c) => !['categories'].includes(c.type));
    },
    defaultFilters() {
      return {
        organization_id: this.organizationId,
      }
    },
    statisticObjects() {
      return [
        ...this.objects.map((obj) => {
          const objType = constants.OBJECT_TYPES.find((t) => t.value == obj.type);
          const roleAction = objType?.role_action;
          return {
            name: obj.extra.name,
            roleAction,
            key: obj.extra.key,
            count: objType.count || this.statistic[`${roleAction}_${obj.extra.key}`] || 0
          }
        }),
        {
          name: 'Danh mục',
          roleAction: ROLE_ACTIONS.CATEGORY,
          count: this.statistic[ROLE_ACTIONS.CATEGORY] || 0
        },
        {
          name: 'Media',
          roleAction: ROLE_ACTIONS.FILE,
          count: this.statistic[ROLE_ACTIONS.FILE] || 5900
        },
        {
          name: 'Thẻ Tag',
          roleAction: ROLE_ACTIONS.TAG,
          count: this.statistic[ROLE_ACTIONS.TAG] || 0
        }
      ]
    },
    times() {
      const today = dayjs()
      const results = Array.from({ length: 7 }, (_, i) =>
        today.subtract(i, "day")
      );
      return results
    },
    timeObjects() {
      return this.objects.map((obj) => {
        const roleAction = constants.OBJECT_TYPES.find((t) => t.value == obj.type)?.role_action;
        return {
          name: obj.extra.name,
          roleAction,
          key: obj.extra.key,
          count: this.times.map((time) => {
            const label = dayjs(time).format('DD-MM-YYYY')
            return {
              label,
              value: this.timeData[`${roleAction}_${obj.extra.key}__${label}`] || 0,
            }
          })
        }
      })
    },
    timesPayload() {
      const results = this.times.map((time) => {
        return this.timeObjects.map((obj) => {
          return {
            document_key: obj.roleAction,
            filters: {
              ...this.defaultFilters,
              type: obj.key,
              created: {
                '$gte': dayjs(time).format('YYYY-MM-DDT00:00:00'),
                '$lt': dayjs(time).format('YYYY-MM-DDT23:59:59')
              }
            },
            query_id: `${obj.key}__${dayjs(time).format('DD-MM-YYYY')}`
          }
        })
      })
      return results.flat()
    },
    category1List() {
      return [
        ...this.allCategories.filter((c) => c.type === "warehouse"),
        {
          id: 'none',
          extra: { name: 'Chưa nhập liệu' }
        }
      ]
    },
    statisticPayload1() {
      return this.category1List.map((c) => ({
        document_key: ROLE_ACTIONS.ARTIFACT,
        filters: {
          type: "artifacts",
          [`extra.kho_luu_tru`]: c.id === 'none' ? null : c.id,
        },
        query_id: c.id
      }))
    },
    statisticObjects1() {
      return this.category1List.map((c) => ({
        name: c.extra.name,
        count: this.statistic1[`${ROLE_ACTIONS.ARTIFACT}_${c.id}`] || 0
      }))
    },
    category2List() {
      return [
        ...this.allCategories.filter((c) => c.type === "xuat_nhap_kho"),
        {
          id: 'none',
          extra: { name: 'Chưa nhập liệu' }
        }
      ]
    },
    statisticPayload2() {
      return this.category2List.map((c) => ({
        document_key: ROLE_ACTIONS.ARTIFACT,
        filters: {
          type: "artifacts",
          [`extra.storage`]: c.id === 'none' ? null : c.id,
        },
        query_id: c.id
      }))
    },
    statisticObjects2() {
      return this.category2List.map((c) => ({
        name: c.extra.name,
        count: this.statistic2[`${ROLE_ACTIONS.ARTIFACT}_${c.id}`] || 0
      }))
    },
  },
  beforeMount() {
    this.loading = true
    Promise.all([
      this.getStatisticData(),
      this.getTimesData(),
      this.getStatisticData1(),
      this.getStatisticData2(),
    ]).finally(() => {
      this.loading = false
    })
  },

  methods: {
    getStatisticData() {
      const items = this.statisticObjects.map((obj) => ({
        document_key: obj.roleAction,
        filters: {
          ...this.defaultFilters,
          type: obj.key
        },
        query_id: obj.key
      }))
      documentUtilServices.post_count({
        items,
      }).then((res) => {
        this.statistic = res.counts;
      })
    },
    getTimesData() {
      documentUtilServices.post_count({
        items: this.timesPayload,
      }).then((res) => {
        this.timeData = res.counts;
      })
    },
    getStatisticData1() {
      if (this.statisticPayload1.length > 0) {
        documentUtilServices.post_count({
          items: this.statisticPayload1,
        }).then((res) => {
          this.statistic1 = res.counts;
        })
      }
    },
    getStatisticData2() {
      if (this.statisticPayload2.length > 0) {
        documentUtilServices.post_count({
          items: this.statisticPayload2,
        }).then((res) => {
          this.statistic2 = res.counts;
        })
      }
    },
  }
};
</script>

<style lang="scss">
.homepage {
  .el-card {
    background-color: transparent !important;
  }
}
</style>
